/**
 * Copyright (c) 2021, Ouster, Inc.
 * All rights reserved.
 */
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import React, { useCallback } from 'react';
import styled from 'styled-components/macro';

import { PaginationInfo } from '../types';

// Right aligned flexbox matching Figma
const ControlsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-size: 12px;
  gap: 11px;
  padding: 14px 0;
  /* Force pagination controls to stays on one line */
  white-space: nowrap;
`;

const ChevronButtonsContainer = styled.div`
  display: flex;
`;

// TODO(pierre) on 2021-04-22: Extend SVGButtonIcon instead
const ChevronButton = styled.button`
  color: var(--ouster-colors-gray-200);
  background-color: transparent;
  border-style: none;
  border-radius: 16px;
  padding: 0px;
  height: 21px;
  width: 21px;

  :disabled {
    color: var(--ouster-colors-gray-600);
  }

  :hover:not(:disabled) {
    background-color: #eeeeee;
    color: var(--ouster-colors-gray-800);
    cursor: pointer;
  }

  :focus:not(:disabled) {
    outline: none;
    color: var(--ouster-colors-gray-800);
  }

  :active:not(:disabled) {
    background-color: var(--ouster-colors-gray-800);
  }

  svg {
    width: 100%;
    height: 100%;
  }
`;

const PaginationControls: React.FC<{
  paginationInfo: PaginationInfo;
  onPageChange: (nextPage: boolean) => void;
}> = ({ paginationInfo, onPageChange }) => {
  const paginationFeedback = useCallback(() => {
    const { currentPage, totalCount, totalPages, perPage } = paginationInfo;

    if (currentPage < 1 || (totalPages !== 0 && currentPage > totalPages)) {
      console.error(`page ${currentPage} is out of range [0 - ${totalPages}]`);
      return '';
    }

    const start = Math.min((currentPage - 1) * perPage + 1, totalCount);
    const end = Math.min(start + perPage - 1, totalCount);

    // Use to LocaleString for more readability e.g. 16323 --> '16,323'
    return `${start.toLocaleString()}—${end.toLocaleString()} of ${totalCount.toLocaleString()}`;
  }, [paginationInfo]);

  return (
    <ControlsContainer>
      {paginationFeedback()}
      <ChevronButtonsContainer>
        <ChevronButton
          type="button"
          onClick={() => {
            onPageChange(false);
          }}
          disabled={paginationInfo.currentPage === 1}
        >
          <ChevronLeft />
        </ChevronButton>
        <ChevronButton
          type="button"
          onClick={() => {
            onPageChange(true);
          }}
          disabled={paginationInfo.currentPage === paginationInfo.totalPages}
        >
          <ChevronRight />
        </ChevronButton>
      </ChevronButtonsContainer>
    </ControlsContainer>
  );
};

export { PaginationControls };
